// src/app/router.tsx
import { createBrowserRouter } from 'react-router-dom';
import { ProtectedRoute } from './protected-route';
import { NotFoundRoute, RouteErrorElement } from './route-error-element';
import { AppShellFrame } from './app-shell';
import { HomeRedirect, TodayRedirect } from './home-redirect';
import { DashboardPage } from '@/dashboard/dashboard-page';
import { EncounterRoute } from '@/encounter/encounter-route';
import { PatientsListPage } from '@/patients/pages/patients-list-page';
import { PatientEditPage } from '@/patients/pages/patient-edit-page';
import { CalendarPage } from '@/scheduling/calendar-page';
import { ViewAppointmentPanelPage } from '@/scheduling/pages/view-appointment-panel-page';
import { AccountSettingsRoute } from '@/settings/account-settings-route';
import { ProviderFeesDetailPage } from '@/billing/pages/provider-fees-detail-page';
import { ForgotPasswordPage } from '@/auth/forgot-password-page';
import { ResetPasswordPage } from '@/auth/reset-password-page';
import { CreatePasswordPage } from '@/auth/create-password-page';
import { TasksPage } from '@/tasks/pages/tasks-page';
import { MessagesDeepLink } from '@/staff-chat/pages/messages-deep-link';

export const router = createBrowserRouter([
  // Public login-recovery routes — rendered WITHOUT ProtectedRoute so they are
  // reachable while signed out (and via the emailed invite/reset links). The
  // login form itself is still shown by ProtectedRoute on the protected `/`
  // branch when unauthenticated; these are its siblings. (login-recovery suite)
  {
    path: '/forgot-password',
    element: <ForgotPasswordPage />,
    errorElement: <RouteErrorElement variant="page" />,
  },
  {
    path: '/reset-password',
    element: <ResetPasswordPage />,
    errorElement: <RouteErrorElement variant="page" />,
  },
  {
    path: '/create-password',
    element: <CreatePasswordPage />,
    errorElement: <RouteErrorElement variant="page" />,
  },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <AppShellFrame />
      </ProtectedRoute>
    ),
    // `page`, not `region`: a throw here means ProtectedRoute or the shell frame itself
    // failed, so there is no rail left to preserve — fill the viewport.
    errorElement: <RouteErrorElement variant="page" />,
    // Canonical nav set + order (Flutter menu_container_screen.dart:259–289):
    // Dashboard · Today · Calendar · Patients · Account Settings. Default landing
    // is Today (source: selectedMenuContent = contents[1]), not Dashboard.
    children: [
      // Home is the ready list at /dashboard for everyone, whatever the switch (home-path.ts, owner 21 Oct).
      { index: true, element: <HomeRedirect /> },
      // Dashboard home — greeting + reused Today worklist + phone-call note
      // capture (Flutter dashboard_module; POST CreateUpdatePhoneCallNotes).
      {
        path: 'dashboard',
        element: <DashboardPage />,
        errorElement: <RouteErrorElement />,
      },
      // `/today` redirects home (owner, 21 Oct): the old three-column Today is no longer a route of its own. Its code
      // stays: the Dashboard still embeds it for a nurse and with the readyList switch OFF (dashboard-page.tsx).
      {
        path: 'today',
        element: <TodayRedirect />,
        errorElement: <RouteErrorElement />,
      },
      // Encounter opens from an appointment tap. Keyed on the appointment (R18, encounter-route.tsx):
      // a different appointment is a fresh mount, so no state from the previous patient survives.
      {
        path: 'encounter/:appointmentId',
        element: <EncounterRoute />,
        errorElement: <RouteErrorElement />,
      },
      {
        path: 'calendar',
        element: <CalendarPage />,
        errorElement: <RouteErrorElement />,
      },
      // Appointment deep-view + edit (PROD-1712/1713) — entered from the
      // calendar popup's pencil button and from AppointmentSearch results.
      {
        path: 'scheduling/appointment/:appointmentId',
        element: <ViewAppointmentPanelPage />,
        errorElement: <RouteErrorElement />,
      },
      // Patients (PROD-1119). PM's nested <Routes> (/, /patients, /patients/new,
      // /patients/edit/:patientId) adapted to PA's data router: list + create +
      // edit as flat children of the AppShell/ProtectedRoute parent.
      {
        path: 'patients',
        element: <PatientsListPage />,
        errorElement: <RouteErrorElement />,
      },
      {
        path: 'patients/new',
        element: <PatientEditPage />,
        errorElement: <RouteErrorElement />,
      },
      {
        path: 'patients/edit/:patientId',
        element: <PatientEditPage />,
        errorElement: <RouteErrorElement />,
      },
      // Task management R1 (23 Oct): My tasks + Team queue. The page itself checks the `?x=tasks` switch (tasks-switch.ts).
      {
        path: 'tasks',
        element: <TasksPage />,
        errorElement: <RouteErrorElement />,
      },
      // Staff chat MVP (SR-14): deep links only. They open the Team dock (on the thread) and go home; the dock is the
      // only staff chat surface. With `?x=staff-chat` off they only go home.
      {
        path: 'messages',
        element: <MessagesDeepLink />,
        errorElement: <RouteErrorElement />,
      },
      {
        path: 'messages/:threadId',
        element: <MessagesDeepLink />,
        errorElement: <RouteErrorElement />,
      },
      // Analytics (2 Nov): the clinic's Programmes and Population analytics, ported from the clinician prototype. The page
      // checks the `?x=analytics` switch itself; loaded lazily, so nothing of it is fetched until it is opened.
      {
        path: 'analytics',
        lazy: () => import('@/analytics-hub/analytics-page'),
        errorElement: <RouteErrorElement />,
      },
      // Account Settings — the logged-in user's self-profile editor (PROD-1200,
      // epic PROD-1199). AccountSettingsRoute dispatches by real role data:
      // doctor → AccountSettingsPage; nurse → NurseAccountSettingsPage (PROD-1206, done).
      {
        path: 'settings',
        element: <AccountSettingsRoute />,
        errorElement: <RouteErrorElement />,
      },
      // Provider Fees — the doctor's OWN per-service fees (PROD-1210). Reached
      // from the Billing tab's "View" link; full-page route (outside the
      // settings shell). `:providerId` is the logged-in doctor's doctorSetupId.
      {
        path: 'billing/master-data/provider-fees/:providerId',
        element: <ProviderFeesDetailPage />,
        errorElement: <RouteErrorElement />,
      },
      // A REAL 404 (Ahmed's ruling, 2026-09-08). This was `<Navigate to="/today" replace />`,
      // which silently swallowed every dead or mistyped link: the user asked for one page,
      // landed on another, and was told nothing.
      { path: '*', element: <NotFoundRoute /> },
    ],
  },
]);
